import React, { useEffect } from 'react';
import { IndustryProductSelectorProps } from './types';
import {
  Alignments,
  Button,
  FlexGrid,
  getClassNameFactory,
  getRehydratableName,
  Heading,
  Rank,
  Sizes,
  Typography,
} from '@dcl/ui';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import ReactHtmlParser from 'react-html-parser';
import { boldTypes, types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { DropDownSelector } from './components/DropDownSelector/DropDownSelector';
import { ButtonColor, ButtonKind } from '@dcl/ui/dist/elements/Button';
import './IndustryProductSelector.css';

export const IndustryProductSelector: React.FC<IndustryProductSelectorProps> = (
  props
) => {
  const {
    title,
    description,
    industries,
    industryDropDownHeader,
    productDropDownHeader,
    productDropDownPlaceholder,
    industryDropDownPlaceholder,
    submitButtonName,
  } = props;

  const [industryOptions, setIndustryOptions] = React.useState<
    { label: string; value: string }[]
  >([]);

  const [productOptions, setProductOptions] = React.useState<
    { label: string; value: string }[]
  >([]);

  const [
    selectedIndustryValue,
    setSelectedIndustryValue,
  ] = React.useState<string>('');

  const [
    selectedProductValue,
    setSelectedProductValue,
  ] = React.useState<string>('');

  const [
    industrySelectorErrorShow,
    setIndustrySelectorErrorShow,
  ] = React.useState<boolean>(false);

  const [
    productSelectorErrorShow,
    setProductSelectorErrorShow,
  ] = React.useState<boolean>(false);

  const [
    industrySelectorErrorMessage,
    setIndustrySelectorErrorMessage,
  ] = React.useState<string>('');

  const [
    productSelectorErrorMessage,
    setProductSelectorErrorMessage,
  ] = React.useState<string>('');

  const [
    productSelectorDisabled,
    setProductSelectorDisabled,
  ] = React.useState<boolean>(true);

  const getClassName = getClassNameFactory(IndustryProductSelector.displayName);

  useEffect(() => {
    const industryOptions = [
      { label: 'All', value: 'All' },
      ...industries.map((industry) => ({
        label: industry.industryName,
        value: industry.industryName,
      })),
    ];
    setIndustryOptions(industryOptions);
  }, [industries]);

  const handleIndustryChange = (value: string) => {
    if (value === 'All') {
      setSelectedIndustryValue(value);
      const allProducts = industries
        .flatMap((industry) =>
          industry.productList.map((product) => ({
            label: product.linkText,
            value:
              industry.industryName +
              '$' +
              product.newTab +
              '$' +
              product.linkPath,
          }))
        )
        .sort((a, b) => a.label.localeCompare(b.label));
      setProductOptions(allProducts);
      setProductSelectorDisabled(false);
      setIndustrySelectorErrorShow(false);
      setIndustrySelectorErrorMessage('');
      return;
    }

    const selectedIndustry = industries.find(
      (industry) => industry.industryName === value
    );
    setSelectedIndustryValue(value);
    if (selectedIndustry) {
      const productOptions = selectedIndustry.productList
        .map((product) => ({
          label: product.linkText,
          value: value + '$' + product.newTab + '$' + product.linkPath,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setProductOptions(productOptions);
      setProductSelectorDisabled(false);

      setIndustrySelectorErrorShow(false);
      setIndustrySelectorErrorMessage('');
    }
  };

  const handleProductChange = (value: string) => {
    setSelectedProductValue(value);
    setProductSelectorErrorShow(false);
    setProductSelectorErrorMessage('');
  };

  const handleButtonClick = (
    selectedIndustry: string,
    selectedProduct: string
  ) => {
    if (selectedIndustry === '') {
      setIndustrySelectorErrorShow(true);
      setIndustrySelectorErrorMessage('Please select an industry');
      return;
    } else {
      setIndustrySelectorErrorShow(false);
      setIndustrySelectorErrorMessage('');
    }

    if (selectedProduct === '') {
      setProductSelectorErrorShow(true);
      setProductSelectorErrorMessage('Please select a product');
      return;
    } else {
      setProductSelectorErrorShow(false);
      setProductSelectorErrorMessage('');
    }

    const productDetails = selectedProduct.split('$');

    if (productDetails[1] === 'true') {
      window.open(productDetails[2], '_blank');
    } else {
      window.location.href = productDetails[2];
    }
  };

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(
        IndustryProductSelector.displayName
      )}
      {...propsToDataAttributes(props)}
    >
      <FlexGrid className={getClassName({ descendantName: 'grid' })} fullWidth>
        <FlexGrid.Row>
          <FlexGrid.Col
            className={getClassName({ descendantName: 'title' })}
            md={12}
          >
            <Heading
              className={getClassName({ descendantName: 'pageTitle' })}
              type={types.Medium}
              boldType={boldTypes.medium}
              rank={Rank.Forth}
            >
              {title}
            </Heading>
          </FlexGrid.Col>

          <FlexGrid.Col
            className={getClassName({ descendantName: 'description' })}
            md={12}
          >
            <Typography
              align={Alignments.LEFT}
              type={Sizes.SMALL}
              tag={TypographyTags.P}
              className={getClassName({ descendantName: 'description' })}
              data-testid="IndustryProductSelector-description"
            >
              {ReactHtmlParser(description)}
            </Typography>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row
          className={getClassName({ descendantName: 'dropDownRow' })}
        >
          <FlexGrid.Col xs={12} md={5} lg={3}>
            <DropDownSelector
              dropdownList={industryOptions}
              placeholderText={industryDropDownPlaceholder}
              showErrorMessage={industrySelectorErrorShow}
              errorMessageText={industrySelectorErrorMessage}
              dropdownLabel={industryDropDownHeader}
              onDropdownValueChange={(value) => handleIndustryChange(value)}
              isDisabled={false}
              maximumVisibleItem={5}
              id={'industry'}
            ></DropDownSelector>
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={5} lg={3}>
            <DropDownSelector
              dropdownList={productOptions}
              placeholderText={productDropDownPlaceholder}
              showErrorMessage={productSelectorErrorShow}
              errorMessageText={productSelectorErrorMessage}
              dropdownLabel={productDropDownHeader}
              onDropdownValueChange={(value) => handleProductChange(value)}
              isDisabled={productSelectorDisabled}
              maximumVisibleItem={5}
              id={'product'}
            ></DropDownSelector>
          </FlexGrid.Col>
          <FlexGrid.Col
            xs={12}
            md={2}
            lg={1}
            className={getClassName({ descendantName: 'buttonCol' })}
          >
            <Button
              kind={ButtonKind.BUTTON}
              block
              color={ButtonColor.PRIMARY}
              onClick={() =>
                handleButtonClick(selectedIndustryValue, selectedProductValue)
              }
            >
              {submitButtonName}
            </Button>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </div>
  );
};

IndustryProductSelector.displayName = 'IndustryProductSelector';
